import type { FC, ReactNode } from 'react'

import { Play } from 'lucide-react'
import { FINISHES } from '@/constants/finishes'
import { FINISH_SEEK } from '@/components/landing/film/timeline'
import { seekFilm } from '@/components/landing/film/film-stage'
import SectionHeading from '@/components/landing/section-heading'

const FinishesSection: FC = (): ReactNode => {
    return (
        <section id='finishes' className='relative overflow-hidden border-t border-white/5 bg-[#08090c] py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        eyebrow='Finishes'
                        title='Three signatures. Endless tailoring.'
                        description='Every finish is applied by hand to order. Choose one of the signatures below, or commission a bespoke tone matched to your car.'
                    />

                    <div className='flex shrink-0 items-center gap-4 self-start rounded-full border border-gold/40 bg-obsidian px-6 py-4 lg:self-auto'>
                        <span className='font-heading text-2xl font-bold text-gold'>100%</span>
                        <span className='font-mono text-[11px] uppercase leading-relaxed tracking-[0.24em] text-titanium-light'>
                            Tailor-made
                            <br />
                            fitment
                        </span>
                    </div>
                </div>

                <ul className='mt-16 grid gap-6 lg:grid-cols-3'>
                    {FINISHES.map((finish) => (
                        <li key={finish.key} className='group flex flex-col overflow-hidden rounded-xl border border-white/5 bg-obsidian transition-colors duration-300 hover:border-gold/30'>
                            <div className='relative aspect-[4/3] overflow-hidden bg-obsidian'>
                                <img
                                    src={finish.image}
                                    alt={finish.imageAlt}
                                    width={1200}
                                    height={900}
                                    loading='lazy'
                                    decoding='async'
                                    className='size-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105 motion-reduce:transition-none'
                                />
                                <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-obsidian via-obsidian/10 to-transparent' aria-hidden='true' />

                                <span className='absolute left-5 top-5 rounded-full bg-obsidian/70 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.24em] text-white/85 backdrop-blur-sm'>
                                    {finish.number} / 03
                                </span>
                                <span className='absolute bottom-5 left-5 h-1 w-16 rounded-full' style={{ background: finish.swatch }} aria-hidden='true' />
                            </div>

                            <div className='flex flex-1 flex-col p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.26em] text-gold'>
                                    {finish.model}
                                </p>
                                <h3 className='mt-3 font-heading text-lg font-bold leading-snug tracking-[0.02em] text-white'>
                                    {finish.name}
                                </h3>
                                <p className='mt-4 text-sm leading-relaxed text-titanium-light'>
                                    {finish.copy}
                                </p>
                                <p className='mt-3 text-sm leading-relaxed text-titanium'>
                                    {finish.detail}
                                </p>
                                <button
                                    type='button'
                                    onClick={() => seekFilm(FINISH_SEEK[finish.key])}
                                    className='mt-8 inline-flex items-center gap-3 self-start font-mono text-[11px] uppercase tracking-[0.24em] text-gold transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                                >
                                    <span className='flex size-8 items-center justify-center rounded-full border border-gold/60 transition-colors group-hover:bg-gold group-hover:text-obsidian'>
                                        <Play className='size-3.5 translate-x-px' />
                                    </span>
                                    Watch in motion
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default FinishesSection
